import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { PointOptionsType, Series, YAxisPlotLinesOptions } from 'highcharts';

import { useBridgeEvents } from '@realTimeDataAccess';
import { PaginatedMeasurementRequestParams, useGetMeasurementPaginated } from '@restDataAccess';
import { MappingCollections, SortDirection } from '@scaleaq/sitecontrol-lib';
import HighchartsReact from 'highcharts-react-official';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import Highcharts from '@/components/chart/highcharts';
import { Z_INDEX } from '@/constants';
import { useEventClassUnitPair, useGetFriendlyProviderName } from '@/hooks';
import { colors, fromApiDateTime, toApiDateTime } from '@/utils';

import { AutoResizeHighChart } from '../../AutoResizeHighChart.tsx';
import {
  setAxisPlotLines,
  syncChartYAxes,
} from '../../chartHelperFunctions/configureChartYAxes.ts';
import { getCappedForwardDuration } from '../../chartHelperFunctions/getCappedForwardDuration.ts';
import { getChartNameWithPeriod } from '../../chartHelperFunctions/getChartNameWithPeriod.ts';
import { useChartOptionsWithLocale } from '../../hooks';
import {
  ChartType,
  ICardDataState,
  intervalToDuration,
  LinearChartConfigType,
  LineDrawStyle,
  SensorData,
} from '../../types.ts';
import { useChartZoom } from '../chartZoom/useChartZoom.ts';
import {
  dateTimeLabelFormats,
  defaultTooltipOptions,
  defaultXAxisOptions,
  getDefaultTooltipPointFormatter,
  getLocaleFromLanguage,
  getLineSeriesStyle,
  getMarkerSymbol,
  lineWidth,
  referenceLineColor,
  updateChartLocale,
} from '../defaultHighChartOptions.ts';
import { referenceLineId } from './linearHighChart/linearHighChart.constants.ts';
import { StyledChartWrapper } from './linearHighChart/linearHighChart.styles.ts';
import { useAnimateLiveChart } from './linearHighChart/useAnimateLiveChart.ts';
import { useLineDrawStyle } from './linearHighChart/useLineDrawStyle.ts';

interface LinearHighChartProps {
  config: LinearChartConfigType;
  setChartDataStat: (props: ICardDataState) => void;
  onChartNameChange?: (chartName: string | undefined) => void;
}

export const LinearHighChart = ({
  config,
  setChartDataStat,
  onChartNameChange,
}: LinearHighChartProps) => {
  const highChartsRef = useRef<HighchartsReact.RefObject>(null);
  const { plot, sensors, type, interval, reference } = config;
  const drawStyle = config.drawStyle ?? (config.pointsOnly ? LineDrawStyle.POINTS : undefined);
  const from = useStableDateTime(config.from);
  const to = useStableDateTime(config.to);
  const drawStyleRef = useLineDrawStyle({ drawStyle, highChartsRef });

  const { t, i18n } = useTranslation(['chartConfig']);
  const { applyBaseRange, followBaseRange } = useChartZoom(highChartsRef);
  const { units } = useEventClassUnitPair();
  const { getFriendlyProviderName } = useGetFriendlyProviderName();

  const plotAsAStringArray = useMemo(() => plot.map(p => p.value), [plot]);
  const sensorAsStringArray = useMemo(() => sensors.map(s => s.value), [sensors]);

  const isLive = useMemo(() => type === ChartType.LIVE, [type]);

  //**** Getting historical data ****//
  const queries = useMemo<PaginatedMeasurementRequestParams[]>(() => {
    const liveChartStartedTime = toApiDateTime(DateTime.now().minus(intervalToDuration[interval]));

    return sensorAsStringArray.flatMap(inventoryId =>
      plotAsAStringArray.map(plot => ({
        inventoryId,
        type: plot,
        start: isLive ? liveChartStartedTime : toApiDateTime(from ?? DateTime.now()),
        end: !isLive ? (to ? toApiDateTime(to) : undefined) : undefined,
        sortDirection: SortDirection.Ascending,
      }))
    );
  }, [interval, from, to, sensorAsStringArray, plotAsAStringArray, isLive]);

  const {
    data: allMeasurements,
    isLoading,
    isError,
    isFetched,
    isRateLimited,
  } = useGetMeasurementPaginated(queries);

  const hasMeasurements = useMemo(
    () => Object.values(allMeasurements).flatMap(plot => Object.values(plot)).length !== 0,
    [allMeasurements]
  );

  //**** Getting Live data ****//
  const forwardDuration = useMemo(() => getCappedForwardDuration(interval), [interval]);

  const eventsToSubscribe = type === ChartType.LIVE ? plotAsAStringArray : [];

  const updateLiveChart = (path: string, eventName: string, data: SensorData | undefined) => {
    try {
      if (!sensorAsStringArray.length || !sensorAsStringArray.some(s => path.indexOf(s) !== -1))
        return;

      highChartsRef.current?.chart?.series.forEach(s => {
        if (!data) return;

        const seriesName = MappingCollections.friendlyProviderNameToClassName[s.name];
        if (eventName.indexOf(seriesName) !== -1) {
          let value = Object.entries(data).find(
            ([key]) => seriesName.toLowerCase().indexOf(key.toLowerCase()) !== -1
          );
          if (!value) {
            value = Object.entries(data).find(([, val]) => typeof val === 'number');
          }
          if (value) {
            const newPointOption: PointOptionsType = {
              x: new Date(data.date).getTime(),
              y: Number(value[1]),
            };

            s.addPoint(newPointOption, false, false, true);
          }
        }
        if (s.name.indexOf('.') !== -1 && s.name.indexOf(eventName) !== -1) {
          const valueName = s.name.split('.')[1];
          s.addPoint([new Date(data.date).getTime(), data[valueName] as number]);
        }
      });

      // After the points are added: this redraws, also while the user is zoomed in.
      followBaseRange({
        min: DateTime.now().minus(intervalToDuration[interval]).toMillis(),
        max: DateTime.now().plus(forwardDuration).toMillis(),
      });
    } catch (e) {
      console.error('ERROR: ', e);
    }
  };

  useBridgeEvents<SensorData, void>(eventsToSubscribe, updateLiveChart);

  useEffect(() => {
    setChartDataStat({
      isLoading: isLoading,
      isError: isError,
      isEmpty: isFetched && !hasMeasurements,
      isRateLimited: isRateLimited,
    });
  }, [hasMeasurements, isError, isFetched, isLoading, isRateLimited, setChartDataStat]);

  const sensorNames = useMemo(
    () =>
      sensors.reduce(
        (acc, sensor) => ({
          ...acc,
          [sensor.value]: sensor.label,
        }),
        {} as Record<string, string>
      ),
    [sensors]
  );

  /**
   * Generate line name for each series
   * Remarks: Ignore the eslint warning here, to keep clean code
   */
  // eslint-disable-next-line react-x/no-unnecessary-use-callback
  const getLineName = useCallback(
    (sensorId: string, provider: string) =>
      sensorAsStringArray.length > 1
        ? (sensorNames[sensorId] ?? sensorId)
        : getFriendlyProviderName(provider),
    [getFriendlyProviderName, sensorAsStringArray.length, sensorNames]
  );

  // Update chart name in parent component with period explanation
  useEffect(() => {
    const baseName = (sensors.length > 1 ? plot[0]?.label : sensors[0]?.label) || undefined;
    const chartName = getChartNameWithPeriod({
      baseName,
      isLive,
      interval,
      from,
      to,
      t,
    });
    onChartNameChange?.(chartName);
  }, [sensors, plot, isLive, interval, from, to, t, onChartNameChange]);

  //**** Set chart options ****//
  useEffect(() => {
    const chart = highChartsRef.current?.chart;

    while (chart?.series.length) {
      chart.series[0]?.remove();
    }

    const locale = getLocaleFromLanguage(i18n.language);

    // Set locale both globally and on the chart instance to ensure it's applied
    // This is critical because existing chart instances may not pick up global setOptions changes
    Highcharts.setOptions({
      lang: {
        locale: locale,
      },
    });

    // Update the chart instance's lang property directly to ensure locale is applied
    updateChartLocale(chart, locale);

    chart?.tooltip.update({
      ...defaultTooltipOptions,
      xDateFormat: '%A, %b %e, %Y %H:%M:%S', // Highcharts format string - will use locale
    });

    const plotLineOptions: YAxisPlotLinesOptions = {
      id: referenceLineId,
      value: reference?.value,
      label: {
        text: reference?.label,
        style: { color: referenceLineColor },
        x: 0,
      },
      color: referenceLineColor,
      dashStyle: 'Dash',
      zIndex: Z_INDEX.base.elevated,
    };

    chart?.xAxis[0]?.update({
      ...defaultXAxisOptions,
      dateTimeLabelFormats,
    });

    const plotTypeToYAxisIndex = syncChartYAxes(chart, plotAsAStringArray, units, {
      plotLines: reference ? [plotLineOptions] : undefined,
    });

    if (hasMeasurements) {
      if (reference) {
        chart?.addSeries({
          id: referenceLineId,
          type: 'line',
          dashStyle: 'Dash',
          color: colors.stroke.warning.solid[100],
          name: `${reference.label || t('chartConfig:linear.labels.reference')}: ${reference.value}`,
          marker: {
            enabled: false,
          },
          lineWidth: lineWidth,
        });

        chart?.legend.update({
          events: {
            itemClick: function (e) {
              const clickedSeries = e.legendItem as Series;
              if (clickedSeries.options.id === referenceLineId) {
                setAxisPlotLines(
                  this.chart.yAxis[0],
                  clickedSeries.visible ? [] : [plotLineOptions]
                );
              }
            },
          },
        });
      }

      const seriesStyle = getLineSeriesStyle(drawStyleRef.current);
      let seriesIndex = 0;

      Object.entries(allMeasurements).forEach(([plot, measurements]) =>
        Object.entries(measurements).forEach(([sensorId, plotMeasurements]) => {
          const unit = units[plot];

          chart?.addSeries({
            name: getLineName(sensorId, plot),
            type: 'line',
            yAxis: plotTypeToYAxisIndex[plot],
            data: plotMeasurements.map(m => [fromApiDateTime(m.timestamp).toMillis(), m.value]),
            tooltip: {
              ...defaultTooltipOptions,
              pointFormatter: getDefaultTooltipPointFormatter(unit),
            },
            ...seriesStyle,
            marker: { ...seriesStyle.marker, symbol: getMarkerSymbol(seriesIndex++) },
          });
        })
      );

      // Ensure locale is set on chart instance right before setExtremes (which triggers formatting)
      updateChartLocale(chart, locale);

      applyBaseRange({
        min: isLive
          ? DateTime.now().minus(intervalToDuration[interval])?.toJSDate()?.valueOf()
          : (from?.toJSDate().valueOf() ?? undefined),
        max: isLive
          ? DateTime.now().plus(forwardDuration).toJSDate().getTime()
          : (to?.toJSDate().valueOf() ?? undefined),
      });
    }

    // Redraw chart to ensure locale changes are applied to existing chart instance
    chart?.redraw();
  }, [
    i18n,
    i18n.language,
    interval,
    getLineName,
    hasMeasurements,
    allMeasurements,
    reference,
    t,
    units,
    /* effect dep */ sensors.length,
    plotAsAStringArray,
    isLive,
    from,
    to,
    forwardDuration,
    applyBaseRange,
    drawStyleRef,
  ]);

  //**** Animation ****//
  useAnimateLiveChart();

  //**** Rendering ****//
  const chartOptionsWithLocale = useChartOptionsWithLocale();

  return (
    <StyledChartWrapper>
      <AutoResizeHighChart
        highcharts={Highcharts}
        options={chartOptionsWithLocale}
        highChartsRef={highChartsRef}
      />
    </StyledChartWrapper>
  );
};

/**
 * Keeps the same DateTime object while the instant is unchanged. A config update (such as the
 * draw style) rebuilds `from`/`to` as new objects for the same instant, which would otherwise
 * re-run the chart effect and reset the zoom.
 */
const useStableDateTime = (value: DateTime | undefined) => {
  const ref = useRef(value);
  if (ref.current?.toMillis() !== value?.toMillis()) ref.current = value;
  return ref.current;
};
